(function () {
  if (customElements.get("common-collection-filter-showcase")) return;

  const PRODUCTS_SECTION = "common-ccfs-products";

  class CommonCollectionFilterShowcase extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productSwiper = null;
      this.productsContainer = this.querySelector("[data-ccfs-products-container]");
      this.showcaseSection = this.querySelector("[data-ccfs-showcase]");
      this.skeleton = this.querySelector("[data-ccfs-skeleton]");
      this.cache = new Map();
      this.loading = false;
      this.showcaseDataReady = false;
      this.activeHandle = this.productsContainer?.dataset.initialCollectionHandle || "";

      this.initShowcase();

      if (this.activeHandle && this.hasRenderableContent()) {
        this.cache.set(this.activeHandle, this.productsContainer.innerHTML);
        if (!this.showcaseDataReady && this.hasProductsContent()) {
          this.dispatchEvent(new CustomEvent("ccfs-showcase-ready", { bubbles: false }));
          this.showcaseDataReady = true;
        }
      }
    }

    getCardConfig() {
      try {
        return JSON.parse(this.dataset.ccfsCardConfig || "{}");
      } catch {
        return {};
      }
    }

    buildProductsUrl(handle) {
      const root = window.Shopify?.routes?.root || "/";
      const base = root.endsWith("/") ? root : `${root}/`;
      return `${base}collections/${handle}?sections=${PRODUCTS_SECTION}`;
    }

    hasProductsContent() {
      return Boolean(
        this.productsContainer?.querySelector(".swiper-slide:not(.ccfs-empty-item), .custom-item:not(.ccfs-empty-item)")
      );
    }

    hasRenderableContent() {
      return Boolean(this.productsContainer?.querySelector(".swiper-slide, .custom-item"));
    }

    getProductSlideCount() {
      return this.productsContainer?.querySelectorAll(".swiper-slide:not(.ccfs-empty-item)").length || 0;
    }

    getEmptyProductsHtml() {
      const message = this.dataset.ccfsEmptyText || "No products found in this collection.";
      return `<div class="swiper-slide custom-item ccfs-empty-item"><div class="all_info"><div class="content"><div class="common-subtitle">${message}</div></div></div></div>`;
    }

    normalizeProductsHtml(html) {
      const trimmed = (html || "").trim();
      return trimmed || this.getEmptyProductsHtml();
    }

    parseSlidesPerView(value, fallback) {
      if (!value || value === "auto") return "auto";
      const num = parseFloat(value);
      return Number.isNaN(num) ? fallback : num;
    }

    getProductSlidesPerView(swiper) {
      if (!swiper) return 1;
      const value = swiper.params.slidesPerView;
      if (value === "auto") {
        const firstSlide = swiper.slides[0];
        if (!firstSlide || !swiper.width) return 1;
        return Math.max(1, swiper.width / firstSlide.offsetWidth);
      }
      return parseFloat(value) || 1;
    }

    updateProductNavigation(swiper) {
      if (!swiper || this.dataset.enableArrow !== "true") return;

      const nextBtn = this.querySelector("[data-ccfs-swiper-next]");
      const prevBtn = this.querySelector("[data-ccfs-swiper-prev]");
      if (!nextBtn || !prevBtn) return;

      const slideCount = swiper.slides?.length || 0;
      if (slideCount === 0) {
        nextBtn.classList.add("swiper-button-disabled");
        prevBtn.classList.add("swiper-button-disabled");
        return;
      }

      try {
        swiper.update();
      } catch (error) {
        console.warn("[CCFS] swiper update failed", error);
        return;
      }

      const slidesPerView = this.getProductSlidesPerView(swiper);
      const maxIndex = Math.max(0, Math.ceil(slideCount - slidesPerView));
      const atEnd = slideCount <= slidesPerView || swiper.activeIndex >= maxIndex;
      const atStart = swiper.activeIndex <= 0;

      nextBtn.classList.toggle("swiper-button-disabled", atEnd);
      prevBtn.classList.toggle("swiper-button-disabled", atStart);
      nextBtn.setAttribute("aria-disabled", atEnd ? "true" : "false");
      prevBtn.setAttribute("aria-disabled", atStart ? "true" : "false");
    }

    showSkeleton(show) {
      if (!this.skeleton) return;
      this.skeleton.classList.toggle("hidden", !show);
    }

    beginLoading() {
      this.setAttribute("data-ccfs-loading", "true");
      this.showSkeleton(true);
      if (this.showcaseSection) {
        this.showcaseSection.classList.remove("loaded");
      }
    }

    markLoaded() {
      this.removeAttribute("data-ccfs-loading");
      this.showSkeleton(false);
      if (this.showcaseSection) {
        this.showcaseSection.classList.add("loaded");
      }
      if (!this.showcaseDataReady && this.hasProductsContent()) {
        this.showcaseDataReady = true;
        this.dispatchEvent(new CustomEvent("ccfs-showcase-ready", { bubbles: false }));
      }
    }

    activateTab(handle) {
      if (!handle) return;
      this.loadCollection(handle);
    }

    destroyProductSwiper() {
      if (this.productSwiper) {
        this.productSwiper.destroy(true, true);
        this.productSwiper = null;
      }
    }

    initProductSwiper() {
      const enableSwiper = this.dataset.enableSwiper === "true";
      if (!enableSwiper || typeof Swiper === "undefined") return;

      const swiperEl = this.querySelector("[data-ccfs-product-swiper]");
      if (!swiperEl) return;

      const slideCount = this.getProductSlideCount();
      if (slideCount === 0) {
        this.destroyProductSwiper();
        return;
      }

      try {
        if (this.productSwiper) {
          this.productSwiper.destroy(true, true);
          this.productSwiper = null;
        }

        swiperEl.classList.remove("swiper-container-initialized", "swiper-container-horizontal");

        const mbSlides = this.parseSlidesPerView(this.dataset.swiperMbNumProducts, 1.15);
        const ipadSlides = this.parseSlidesPerView(this.dataset.swiperIpadNumProducts, 1.5);
        const pcSlides = this.parseSlidesPerView(this.dataset.swiperPcNumProducts, 4);
        const pcGap = parseInt(this.dataset.swiperPcGapProducts, 10) || 20;

        const config = {
          slidesPerView: pcSlides,
          spaceBetween: pcGap,
          loop: false,
          observer: true,
          observeParents: true,
          watchOverflow: true,
          resistanceRatio: 0,
          navigation: {},
          scrollbar: {},
          breakpoints: {
            589: {
              slidesPerView: mbSlides,
              spaceBetween: 10,
            },
            1024: {
              slidesPerView: ipadSlides,
              spaceBetween: 15,
            },
          },
          on: {
            init: () => {
              window.requestAnimationFrame(() => this.updateProductNavigation(this.productSwiper));
            },
            slideChangeTransitionEnd: () => {
              const swiper = this.productSwiper;
              if (!swiper || !swiper.slides?.length) return;
              const maxIndex = Math.max(0, Math.ceil(swiper.slides.length - this.getProductSlidesPerView(swiper)));
              if (swiper.activeIndex > maxIndex) {
                swiper.slideTo(maxIndex, 0);
              }
              this.updateProductNavigation(swiper);
            },
            resize: () => {
              this.updateProductNavigation(this.productSwiper);
            },
            imagesReady: () => {
              this.updateProductNavigation(this.productSwiper);
            },
          },
        };

        if (this.dataset.enableArrow === "true") {
          const nextBtn = this.querySelector("[data-ccfs-swiper-next]");
          const prevBtn = this.querySelector("[data-ccfs-swiper-prev]");
          if (nextBtn && prevBtn) {
            config.navigation = { nextEl: nextBtn, prevEl: prevBtn };
          }
        }

        if (this.dataset.enableScrollbar === "true") {
          const scrollbarEl = this.querySelector("[data-ccfs-swiper-scrollbar]");
          if (scrollbarEl) {
            config.scrollbar = { el: scrollbarEl, draggable: true };
          }
        }

        this.productSwiper = new Swiper(swiperEl, config);
        window.setTimeout(() => this.updateProductNavigation(this.productSwiper), 50);
        window.setTimeout(() => this.updateProductNavigation(this.productSwiper), 300);
      } catch (error) {
        console.error("[CCFS] product swiper init failed", error);
      }
    }

    bootstrapShowcase() {
      if (!this.productsContainer) return;
      if (!this.productsContainer.innerHTML.trim()) {
        this.productsContainer.innerHTML = this.getEmptyProductsHtml();
      }
      this.initProductSwiper();

      if (this.getProductSlideCount() === 0) {
        const nextBtn = this.querySelector("[data-ccfs-swiper-next]");
        const prevBtn = this.querySelector("[data-ccfs-swiper-prev]");
        if (nextBtn) {
          nextBtn.classList.add("swiper-button-disabled");
          nextBtn.setAttribute("aria-disabled", "true");
        }
        if (prevBtn) {
          prevBtn.classList.add("swiper-button-disabled");
          prevBtn.setAttribute("aria-disabled", "true");
        }
      }

      this.markLoaded();
    }

    initShowcase() {
      if (!this.productsContainer) return;

      if (this.hasRenderableContent()) {
        this.bootstrapShowcase();
        return;
      }

      const runBootstrap = () => {
        if (!this.showcaseSection?.classList.contains("loaded")) {
          this.bootstrapShowcase();
        }
      };

      const height = window.innerHeight * 2;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            runBootstrap();
            observer.disconnect();
          }
        },
        { threshold: 0, rootMargin: `${height}px 0px 0px 0px` }
      );
      observer.observe(this.showcaseSection || this.productsContainer);

      window.setTimeout(runBootstrap, 1200);
    }

    trimProductsHtml(html) {
      const config = this.getCardConfig();
      const limit = parseInt(config.products_limit, 10) || 20;
      const temp = document.createElement("div");
      temp.innerHTML = html;
      const slides = temp.querySelectorAll(".swiper-slide:not(.ccfs-empty-item)");
      if (slides.length > limit) {
        slides.forEach((slide, index) => {
          if (index >= limit) slide.remove();
        });
      }
      return temp.innerHTML.trim() ? temp.innerHTML : this.getEmptyProductsHtml();
    }

    applyCardConfigPatch(container) {
      const config = this.getCardConfig();
      if (!container) return;

      if (config.learn_more_text) {
        container.querySelectorAll(".learn_more_btn a").forEach((link) => {
          link.textContent = config.learn_more_text;
        });
      }

      if (config.show_learn_more === false) {
        container.querySelectorAll(".learn_more_btn").forEach((el) => el.remove());
      }

      if (config.show_product_price === false) {
        container.querySelectorAll(".product-price-box, .img-show-discount-price").forEach((el) => el.remove());
      }

      if (config.show_true_price_percentage === false) {
        container.querySelectorAll(".ccfs-discount-percent").forEach((el) => {
          el.hidden = true;
        });
        container.querySelectorAll(".ccfs-discount-amount").forEach((el) => {
          el.hidden = false;
        });
      } else if (config.show_true_price_percentage === true) {
        container.querySelectorAll(".ccfs-discount-percent").forEach((el) => {
          el.hidden = false;
        });
        container.querySelectorAll(".ccfs-discount-amount").forEach((el) => {
          el.hidden = true;
        });
      }
    }

    extractProductsHtml(responseText) {
      const trimmed = responseText.trim();

      if (trimmed.startsWith("{")) {
        try {
          const sections = JSON.parse(trimmed);
          const sectionHtml =
            sections[PRODUCTS_SECTION] ||
            Object.values(sections).find(
              (value) => typeof value === "string" && value.includes("data-ccfs-products-content")
            );
          if (sectionHtml) {
            return this.extractProductsHtmlFromDocument(sectionHtml);
          }
        } catch (error) {
          console.warn("[CCFS] sections JSON parse failed", error);
        }
      }

      return this.extractProductsHtmlFromDocument(trimmed);
    }

    extractProductsHtmlFromDocument(html) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const content =
        doc.querySelector("[data-ccfs-products-content]") ||
        doc.querySelector(".ccfs-products-content") ||
        doc.querySelector("#shopify-section-common-ccfs-products [data-ccfs-products-content]") ||
        doc.querySelector(".ccfs-products-section [data-ccfs-products-content]");
      return content ? content.innerHTML : "";
    }

    async loadCollection(handle) {
      if (!handle || !this.productsContainer) return;
      if (handle === this.activeHandle && this.hasRenderableContent()) {
        this.bootstrapShowcase();
        return;
      }

      if (this.cache.has(handle)) {
        this.destroyProductSwiper();
        this.productsContainer.innerHTML = this.cache.get(handle);
        this.activeHandle = handle;
        this.applyCardConfigPatch(this.productsContainer);
        this.bootstrapShowcase();
        return;
      }

      if (this.loading) return;
      this.loading = true;
      this.beginLoading();

      try {
        let response = await fetch(this.buildProductsUrl(handle));
        if (!response.ok) throw new Error(`Failed to load collection: ${handle}`);

        let html = this.normalizeProductsHtml(this.trimProductsHtml(this.extractProductsHtml(await response.text())));

        if (!html.includes("swiper-slide")) {
          const root = window.Shopify?.routes?.root || "/";
          const base = root.endsWith("/") ? root : `${root}/`;
          const fallbackUrl = `${base}collections/${handle}?section_id=${PRODUCTS_SECTION}`;
          response = await fetch(fallbackUrl);
          if (!response.ok) throw new Error(`Failed to load collection fallback: ${handle}`);
          html = this.normalizeProductsHtml(this.trimProductsHtml(this.extractProductsHtml(await response.text())));
        }

        this.destroyProductSwiper();
        this.productsContainer.innerHTML = html;
        this.applyCardConfigPatch(this.productsContainer);
        this.cache.set(handle, this.productsContainer.innerHTML);
        this.activeHandle = handle;
        this.bootstrapShowcase();
      } catch (error) {
        console.error("[CCFS]", error);
        this.productsContainer.innerHTML =
          '<div class="swiper-slide custom-item ccfs-empty-item"><div class="all_info"><div class="content"><div class="common-subtitle">Failed to load products.</div></div></div></div>';
        this.bootstrapShowcase();
      } finally {
        this.loading = false;
      }
    }
  }

  customElements.define("common-collection-filter-showcase", CommonCollectionFilterShowcase);
})();
